import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Stethoscope, ArrowRight, RotateCcw } from "lucide-react";
import { DIAGNOSTIC_QUESTIONS } from "@/data/staticData";
import { toast } from "sonner";

export default function DiagnosticPage() {
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState([]);
  const [totalScore, setTotalScore] = useState(0);

  const start = async () => {
    try {
      const { data } = await API.get("/user/quota/diagnostics");
      if (!data.allowed) { toast.error(`Limite de diagnostics atteinte (${data.limit}/jour).`); return; }
    } catch {}
    setStep(0); setAnswers([]); setTotalScore(0);
  };

  const handleAnswer = async (option) => {
    const newAnswers = [...answers, { question: DIAGNOSTIC_QUESTIONS[step].question, answer: option.label, score: option.score }];
    const newScore = totalScore + option.score;
    setAnswers(newAnswers);
    setTotalScore(newScore);

    if (step < DIAGNOSTIC_QUESTIONS.length - 1) {
      setStep(s => s + 1);
    } else {
      setStep(DIAGNOSTIC_QUESTIONS.length);
      try { await API.post("/diagnostic", { answers: newAnswers }); } catch {}
    }
  };

  const getRiskLevel = (score) => {
    if (score <= 4) return { level: "Faible", color: "var(--ia-risk-minimal)", bg: "bg-green-50", text: "text-green-700" };
    if (score <= 10) return { level: "Modere", color: "var(--ia-risk-high)", bg: "bg-amber-50", text: "text-amber-700" };
    return { level: "Eleve", color: "var(--ia-risk-unacceptable)", bg: "bg-red-50", text: "text-red-700" };
  };

  const maxScore = DIAGNOSTIC_QUESTIONS.reduce((sum, q) => sum + Math.max(...q.options.map(o => o.score)), 0);
  const finished = step >= DIAGNOSTIC_QUESTIONS.length;
  const risk = getRiskLevel(totalScore);

  if (step === -1) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="diagnostic-page">
        <div className="text-center animate-fade-in mt-12">
          <div className="w-16 h-16 bg-[var(--ia-navy)]/5 rounded-full flex items-center justify-center mx-auto mb-6">
            <Stethoscope className="h-8 w-8 text-[var(--ia-navy)]" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Diagnostic Express</h1>
          <p className="text-[var(--ia-text-muted)] mb-6">{DIAGNOSTIC_QUESTIONS.length} questions pour évaluer rapidement le risque de votre projet IA</p>
          <Button onClick={start} className="bg-[var(--ia-navy)]" data-testid="diagnostic-start-btn">Commencer le diagnostic</Button>
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="diagnostic-page">
        <div className="animate-fade-in">
          <div className="text-center mb-8">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 ${risk.bg}`}>
              <span className="text-xl font-bold" style={{ color: risk.color }}>Risque {risk.level}</span>
            </div>
            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Résultat du diagnostic</h2>
            <p className="text-[var(--ia-text-muted)]">Score : {totalScore}/{maxScore}</p>
          </div>

          <Card className="border-[var(--ia-border)] mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Niveau de risque</span>
                <Badge className={`${risk.bg} ${risk.text} border-0`}>{risk.level}</Badge>
              </div>
              <Progress value={(totalScore / maxScore) * 100} className="h-2" />
            </CardContent>
          </Card>

          <Card className="border-[var(--ia-border)] mb-6">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold mb-3">Récap des réponses</h3>
              <div className="space-y-2">
                {answers.map((a, i) => (
                  <div key={i} className="flex items-start justify-between text-xs gap-4 py-2 border-b border-[var(--ia-border)] last:border-0">
                    <span className="text-slate-600 flex-1">{a.question}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-800 font-medium text-right">{a.answer}</span>
                      <Badge variant="outline" className={`text-[10px] ${a.score >= 3 ? "text-red-600 border-red-200" : a.score >= 1 ? "text-amber-600 border-amber-200" : "text-green-600 border-green-200"}`}>
                        +{a.score}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <Button onClick={() => { setStep(-1); setAnswers([]); setTotalScore(0); }} className="bg-[var(--ia-navy)]" data-testid="diagnostic-restart-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> Nouveau diagnostic
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const q = DIAGNOSTIC_QUESTIONS[step];
  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="diagnostic-page">
      <div className="mb-6 animate-fade-in">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-[var(--ia-muted)]">Question {step + 1}/{DIAGNOSTIC_QUESTIONS.length}</span>
        </div>
        <Progress value={((step + 1) / DIAGNOSTIC_QUESTIONS.length) * 100} className="h-1.5" />
      </div>

      <Card className="border-[var(--ia-border)] animate-fade-in" data-testid="diagnostic-question-card">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{q.question}</h2>
          <div className="space-y-3">
            {q.options.map((opt, i) => (
              <button key={i} onClick={() => handleAnswer(opt)} data-testid={`diagnostic-option-${i}`}
                className="w-full p-4 rounded-md border border-[var(--ia-border)] bg-white hover:border-[var(--ia-blue)] hover:bg-blue-50/30 text-left text-sm transition-all">
                {opt.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
